import React, { useState } from "react";
import {
  Box,
  Typography,
  Link as MuiLink,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
  Badge,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ScaleIcon from "@mui/icons-material/Scale";
import LinkIcon from "@mui/icons-material/Link";
import { getSportsIcon } from "../../constants";
import ToggleableDate from "../ToggleableDate";
import moment from "moment";
import { Link } from "react-router-dom";

const SGMHeader = ({
  eventName,
  eventDateTime,
  eventData,
  handleBack,
  triggerRefresh,
  loading,
  anchorEl,
  handleRelatedClick,
  handleRelatedClose,
  handleRelatedSelect,
  onOpenBookieWeightsModal,
  hasCustomBookieWeights,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);

  const relatedEvents =
    eventData && eventData.relatedEvents ? eventData.relatedEvents : [];
  const hasRelatedEvents = relatedEvents.length > 0;

  const sport = eventData?.sport;
  const sportIcon = getSportsIcon(sport);

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleMobileAction = (action) => {
    handleMobileMenuClose();
    action();
  };

  if (isMobile) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          mb: 3,
          gap: 2,
        }}
      >
        {/* Mobile header - primary actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            minHeight: 56,
            gap: 1,
          }}
        >
          {/* Back button and title */}
          <Box
            sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0 }}
          >
            <IconButton
              onClick={handleBack}
              sx={{
                mr: 1.5,
                color: theme.palette.primary.main,
                minWidth: 44,
                minHeight: 44,
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant={isSmallMobile ? "h6" : "h5"}
              sx={{
                fontWeight: "bold",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
            >
              {eventName || "Loading event..."}
            </Typography>
          </Box>

          {/* Primary actions - Refresh + Menu */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Refresh data">
              <IconButton
                onClick={triggerRefresh}
                disabled={loading}
                sx={{
                  color: theme.palette.primary.main,
                  minWidth: 44,
                  minHeight: 44,
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="More options">
              <IconButton
                onClick={handleMobileMenuOpen}
                sx={{
                  color: theme.palette.primary.main,
                  minWidth: 44,
                  minHeight: 44,
                }}
              >
                <Badge
                  color="primary"
                  variant="dot"
                  invisible={!hasCustomBookieWeights}
                >
                  <MoreVertIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Event metadata row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
            px: 1,
          }}
        >
          {/* Date and sport */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flex: 1,
              minWidth: 0,
            }}
          >
            {sportIcon && (
              <Box
                component="span"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: theme.palette.text.secondary,
                  flexShrink: 0,
                }}
              >
                {sportIcon}
              </Box>
            )}
            <ToggleableDate
              date={eventDateTime}
              showTime
              color="textSecondary"
            />
          </Box>

          {/* Related events - mobile optimized */}
          {hasRelatedEvents && (
            <Button
              variant="outlined"
              size="small"
              endIcon={<ExpandMoreIcon />}
              onClick={handleRelatedClick}
              sx={{
                minHeight: 36,
                fontSize: "0.875rem",
              }}
            >
              Related ({relatedEvents.length})
            </Button>
          )}
        </Box>

        {/* Mobile overflow menu */}
        <Menu
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={handleMobileMenuClose}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <MenuItem
            onClick={() =>
              handleMobileAction(() => {
                if (eventData?.eventId) {
                  window.open(`/event/${eventData.eventId}`, "_blank");
                }
              })
            }
            disabled={!eventData?.eventId}
          >
            <LinkIcon sx={{ mr: 2 }} />
            Event Details
          </MenuItem>
          <MenuItem
            onClick={() => handleMobileAction(onOpenBookieWeightsModal)}
          >
            <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
              <ScaleIcon sx={{ mr: 2 }} />
              Bookie Weights
              {hasCustomBookieWeights && (
                <Badge color="primary" variant="dot" sx={{ ml: 1 }} />
              )}
            </Box>
          </MenuItem>
        </Menu>

        {/* Related events menu */}
        {hasRelatedEvents && (
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleRelatedClose}
            MenuListProps={{
              "aria-labelledby": "related-events-button",
            }}
            PaperProps={{
              sx: {
                maxWidth: "calc(100vw - 32px)",
                maxHeight: "50vh",
              },
            }}
          >
            {relatedEvents.map((event) => (
              <MenuItem
                key={event.id}
                onClick={() => handleRelatedSelect(event.id)}
                sx={{
                  bgcolor:
                    event.id === eventData?.eventId
                      ? "action.selected"
                      : "inherit",
                  py: 1.5,
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", width: "100%" }}
                >
                  <Typography sx={{ flex: 1, mr: 1 }}>{event.name}</Typography>
                  {event.id === eventData?.eventId && (
                    <Chip
                      label="Current"
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
              </MenuItem>
            ))}
          </Menu>
        )}
      </Box>
    );
  }

  // Desktop layout
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        mb: 3,
        gap: 2,
      }}
    >
      {/* Desktop top row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            onClick={handleBack}
            sx={{ mr: 1, color: theme.palette.primary.main }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            {eventName || "Loading event..."}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="View event details page">
            <Button
              component={Link}
              to={eventData?.eventId ? `/event/${eventData.eventId}` : "#"}
              variant="outlined"
              size="medium"
              startIcon={<LinkIcon />}
              sx={{ whiteSpace: "nowrap" }}
              disabled={!eventData?.eventId}
            >
              Event Details
            </Button>
          </Tooltip>

          <Tooltip title="Customize bookie weights for EV calculations">
            <Badge
              color="primary"
              variant="dot"
              invisible={!hasCustomBookieWeights}
            >
              <Button
                onClick={onOpenBookieWeightsModal}
                variant="outlined"
                size="medium"
                startIcon={<ScaleIcon />}
                sx={{ whiteSpace: "nowrap" }}
              >
                Bookie Weights
              </Button>
            </Badge>
          </Tooltip>

          <Tooltip title="Refresh data">
            <IconButton
              onClick={triggerRefresh}
              disabled={loading}
              sx={{ color: theme.palette.primary.main }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Desktop metadata row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {sportIcon && (
            <Box
              component="span"
              sx={{
                display: "flex",
                alignItems: "center",
                color: theme.palette.text.secondary,
              }}
            >
              {sportIcon}
            </Box>
          )}
          <ToggleableDate date={eventDateTime} showTime color="textSecondary" />
        </Box>

        {hasRelatedEvents && (
          <Box>
            <Button
              variant="outlined"
              endIcon={<ExpandMoreIcon />}
              onClick={handleRelatedClick}
              size="medium"
              sx={{ whiteSpace: "nowrap" }}
            >
              Related Events ({relatedEvents.length})
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleRelatedClose}
              MenuListProps={{
                "aria-labelledby": "related-events-button",
              }}
            >
              {relatedEvents.map((event) => (
                <MenuItem
                  key={event.id}
                  onClick={() => handleRelatedSelect(event.id)}
                  sx={{
                    bgcolor:
                      event.id === eventData?.eventId
                        ? "action.selected"
                        : "inherit",
                  }}
                >
                  <Box sx={{ mr: 1 }}>{event.name}</Box>
                  {event.id === eventData?.eventId && (
                    <Chip
                      label="Current"
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SGMHeader;
